// material-ui
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import ButtonBase from '@mui/material/ButtonBase';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// project imports
import Navigation from './Navigation';
import SimpleBar from 'components/third-party/SimpleBar';
import Avatar from 'components/@extended/Avatar';
import IconButton from 'components/@extended/IconButton';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import useAuth from 'hooks/useAuth';
import { useGetMenuMaster } from 'api/menu';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import avatar1 from 'assets/images/users/avatar-1.png';

// ==============================|| DRAWER CONTENT ||============================== //

export default function DrawerContent() {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    // Only toggle if the drawer is shrunk
    if (!drawerOpen) {
      setOpen((prevOpen) => !prevOpen);
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate('/login', { replace: true });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <SimpleBar sx={{ '& .simplebar-content': { display: 'flex', flexDirection: 'column' }, height: '100%' }}>
          <Navigation />
        </SimpleBar>
      </Box>
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', p: 1 }}>
        <CardContent sx={{ px: drawerOpen ? 2 : 0, py: 2, '&:last-child': { pb: 2 } }}>
          <Stack direction={drawerOpen ? 'row' : 'column'} spacing={drawerOpen ? 1 : 2} sx={{ justifyContent: drawerOpen ? 'space-between' : 'center', alignItems: 'center' }}>
            <ButtonBase
              ref={anchorRef}
              onClick={handleToggle}
              disableRipple={drawerOpen}
              sx={{ p: 0.5, borderRadius: 1, cursor: drawerOpen ? 'default' : 'pointer' }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ overflow: 'hidden' }}>
                <Avatar alt="profile user" src={avatar1} sx={{ width: 32, height: 32 }} />
                {drawerOpen && (
                  <Stack sx={{ overflow: 'hidden', textAlign: 'left' }}>
                    <Typography variant="subtitle2" noWrap>{user?.name || user?.email || 'User'}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {user?.role || 'Survey Admin'}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </ButtonBase>
            <Tooltip title="Logout" placement={drawerOpen ? 'top' : 'right'}>
              <IconButton size="large" sx={{ color: 'text.primary' }} onClick={handleLogout}>
                <LogoutOutlined />
              </IconButton>
            </Tooltip>
          </Stack>

          <Popper
            placement="right-end"
            open={open && !drawerOpen}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 14] } }] }}
          >
            {({ TransitionProps }) => (
              <Transitions type="grow" position="bottom-left" in={open && !drawerOpen} {...TransitionProps}>
                <Paper sx={(theme) => ({ boxShadow: theme.vars.customShadows.z1, width: 290, minWidth: 240, maxWidth: { xs: 250, md: 290 } })}>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MainCard elevation={0} border={false} content={false}>
                      <CardContent sx={{ px: 2.5, py: 2, '&:last-child': { pb: 2 } }}>
                        <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                          <Grid>
                            <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
                              <Avatar alt="profile user" src={avatar1} sx={{ width: 32, height: 32 }} />
                              <Stack>
                                <Typography variant="h6">{user?.name || user?.email || 'User'}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {user?.role || 'Survey Admin'}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Grid>
                          <Grid>
                            <Tooltip title="Logout">
                              <IconButton size="large" sx={{ color: 'text.primary' }} onClick={handleLogout}>
                                <LogoutOutlined />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </MainCard>
                  </ClickAwayListener>
                </Paper>
              </Transitions>
            )}
          </Popper>
        </CardContent>
      </Box>
    </Box>
  );
}
